import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatCard } from "@/components/common";
import { rupiah } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Landmark, Wallet } from "lucide-react";

export default function CashBank() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [coa, setCoa] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = can("finance.cashbank.manage");
  const load = () => { setRows(null); api.get("/accounting/cash-bank").then((r) => setRows(r.data)); };
  useEffect(() => { load(); api.get("/accounting/chart-of-accounts").then((r) => setCoa(r.data.filter((a) => a.allow_posting && a.type === "ASSET"))); }, []);
  const total = (rows || []).reduce((s, a) => s + (a.balance || 0), 0);

  return (
    <div>
      <PageHeader title="Kas & Bank" subtitle="Saldo kas dan rekening bank perusahaan (Posisi Stok Saat Ini)." testId="cashbank-header">
        {canManage && <Button onClick={() => setOpen(true)} data-testid="add-cashbank-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Rekening Baru</Button>}
      </PageHeader>
      {!rows ? <Loading /> : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard label="Total Kas & Bank" value={rupiah(total)} icon={Landmark} accent="indigo" testId="cb-total" />
          </div>
          {rows.length === 0 ? <EmptyState title="Belum ada rekening" icon={Wallet} /> : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((a) => (
                <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`cb-card-${a.account_code}`}>
                  <div className="flex items-center gap-2">
                    {a.kind === "BANK" ? <Landmark className="h-4 w-4 text-indigo-500" /> : <Wallet className="h-4 w-4 text-emerald-500" />}
                    <p className="font-display text-sm font-semibold text-slate-800">{a.name}</p>
                    <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{a.kind === "BANK" ? "Bank" : "Kas"}</span>
                  </div>
                  <p className="mt-3 text-2xl font-bold text-slate-900">{rupiah(a.balance || 0)}</p>
                  <p className="mt-1 text-xs text-slate-400">Akun {a.account_code}{a.account_number ? ` · ${a.account_number}` : ""}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {open && <CBDialog coa={coa} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function CBDialog({ coa, onClose, onSaved }) {
  const [f, setF] = useState({ account_code: "", kind: "BANK", name: "", bank_name: "", account_number: "", account_holder: "SPH Ascendra", currency: "IDR", opening_balance: 0 });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.account_code || !f.name) return toast.error("Akun CoA & nama wajib");
    setSaving(true);
    try { await api.post("/accounting/cash-bank", { ...f, opening_balance: Number(f.opening_balance) || 0 }); toast.success("Rekening dibuat"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Rekening Kas/Bank Baru</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-500">Jenis</Label><Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}><SelectTrigger className="mt-1" data-testid="cb-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="BANK">Bank</SelectItem><SelectItem value="CASH">Kas</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Akun CoA</Label><Select value={f.account_code} onValueChange={(v) => setF({ ...f, account_code: v })}><SelectTrigger className="mt-1" data-testid="cb-coa"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{coa.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Nama Rekening</Label><Input className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cb-name" /></div>
          <div><Label className="text-xs text-slate-500">No. Rekening</Label><Input className="mt-1" value={f.account_number} onChange={(e) => setF({ ...f, account_number: e.target.value })} data-testid="cb-number" /></div>
          <div><Label className="text-xs text-slate-500">Saldo Awal</Label><Input type="number" className="mt-1" value={f.opening_balance} onChange={(e) => setF({ ...f, opening_balance: e.target.value })} data-testid="cb-opening" /></div>
        </div>
        <p className="text-xs text-slate-400">Saldo awal membuat jurnal pembukaan berimbang (Debit rekening, Kredit Modal 3100).</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-cashbank-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
